// @ts-nocheck
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Plus, Copy, AlertCircle, MoreHorizontal, Eye, Edit, Archive, Trash2, Calendar, MapPin, Send, Users, IdCard, CalendarClock, ListOrdered, ClipboardCheck, CircleDollarSign, Table2, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { CloneEventDialog } from "@/components/admin-dashboard/CloneEventDialog";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { EventOpsDialog, type EventOpsPanel } from "@/components/event-ops/EventOpsDialog";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type EventStatus = "draft" | "published" | "archived" | "cancelled";

interface EventRow {
  id: string;
  name: string;
  status: EventStatus;
  start_date: string | null;
  end_date: string | null;
  venue_city: string | null;
  venue_state: string | null;
  unique_teams_registered: number;
  total_registrations: number;
  total_roster_size: number;
  is_active: boolean;
}

const statusOptions: { value: EventStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: "bg-secondary text-foreground/60",
    published: "bg-success-light text-success",
    registration_open: "bg-info-light text-info",
    in_progress: "bg-warning-light text-warning",
    completed: "bg-muted text-muted-foreground",
    archived: "bg-muted text-muted-foreground",
    cancelled: "bg-error-light text-error",
  };
  const labels: Record<string, string> = {
    draft: "Draft",
    published: "Published",
    registration_open: "Registration Open",
    in_progress: "In Progress",
    completed: "Completed",
    archived: "Archived",
    cancelled: "Cancelled",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-muted text-muted-foreground"}`}>
      {labels[status] || status}
    </span>
  );
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return "Date TBD";
  const s = new Date(start + "T00:00:00");
  const sFormatted = format(s, "MMM d");
  if (end && end !== start) {
    const e = new Date(end + "T00:00:00");
    const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    return sameMonth ? `${sFormatted}–${format(e, "d, yyyy")}` : `${sFormatted} – ${format(e, "MMM d, yyyy")}`;
  }
  return format(s, "MMM d, yyyy");
}

export default function RLeventsTournamentsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [selectedStatuses, setSelectedStatuses] = useState<EventStatus[]>([]);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<string | undefined>();
  const [cloneSourceName, setCloneSourceName] = useState<string | undefined>();
  const [opsTarget, setOpsTarget] = useState<{ id: string; panel: EventOpsPanel } | null>(null);
  const { scopeOrgId, ready } = useAdminOrgScope();

  const { data: events, isLoading } = useQuery({
    queryKey: ["admin-events-list", activeTab, scopeOrgId],
    enabled: ready,
    queryFn: async () => {
      let q = supabase
        .from("v_event_admin_dashboard")
        .select("id, name, slug, status, start_date, end_date, venue_city, venue_state, unique_teams_registered, total_registrations, total_roster_size, is_active")
        .is("deleted_at", null)
        .order("start_date", { ascending: false });

      // Org admin -> scoped to their org. Super admin (scopeOrgId null) -> all.
      if (scopeOrgId) {
        q = q.eq("organization_id", scopeOrgId);
      }

      if (activeTab === "active") {
        q = q.eq("is_active", true).not("status", "in", '("archived","cancelled")');
      } else {
        q = q.or("is_active.eq.false,status.eq.archived");
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data as EventRow[]) || [];
    },
  });

  const eventIds = (events ?? []).map((e) => e.id);

  // Batched counts for all visible events (3 queries total, not 3 per card)
  const { data: opsCounts } = useQuery({
    queryKey: ["admin-events-ops-counts", eventIds.join(",")],
    enabled: eventIds.length > 0,
    queryFn: async () => {
      const [wl, tr, reg, media, sched] = await Promise.all([
        supabase
          .from("event_waitlist")
          .select("event_id, status")
          .in("event_id", eventIds),
        supabase
          .from("event_team_tracking")
          .select("event_id, tracking_type, payment_status, expected_amount, amount_received")
          .in("event_id", eventIds),
        supabase
          .from("event_registrations_v2")
          .select("event_id, payment_status")
          .in("event_id", eventIds),
        supabase
          .from("media_credentials")
          .select("event_id, status")
          .in("event_id", eventIds),
        supabase
          .from("schedule_constraints")
          .select("event_id")
          .is("deleted_at", null)
          // Only requests an operator can act on — unpaid (pending_payment)
          // and superseded rows are hidden from the list, so exclude them here.
          .in("status", ["pending_review", "approved", "partial", "denied"])
          .in("event_id", eventIds),

      ]);

      const map: Record<
        string,
        { waitlist: number; tracking: number; payLater: number; credentials: number; scheduling: number }
      > = {};
      const bump = (id: string, key: "waitlist" | "tracking" | "payLater" | "credentials" | "scheduling") => {
        if (!map[id]) map[id] = { waitlist: 0, tracking: 0, payLater: 0, credentials: 0, scheduling: 0 };
        map[id][key] += 1;
      };

      for (const r of wl.data ?? []) {
        if (r.status === "waiting" || r.status === "offered") bump(r.event_id, "waitlist");
      }
      for (const r of tr.data ?? []) {
        bump(r.event_id, "tracking");
        const paid = ["paid", "not_required", "waived"].includes(r.payment_status);
        if (r.tracking_type !== "comp" && !paid) bump(r.event_id, "payLater");
      }
      for (const r of reg.data ?? []) {
        if (!["paid", "refunded", "waived"].includes(r.payment_status)) bump(r.event_id, "payLater");
      }
      for (const r of media.data ?? []) {
        if (r.status !== "denied") bump(r.event_id, "credentials");
      }
      for (const r of sched.data ?? []) {
        bump(r.event_id, "scheduling");
      }
      return map;
    },
  });

  /**
   * media_credentials carries no table grants for `authenticated`, so a direct
   * browser read comes back empty with a 200 — which is why the badge stayed
   * missing while the Credentials screen (which reads through the sheet-tab
   * edge function) listed all three. Count through the same function.
   */
  const { data: credentialCounts } = useQuery({
    queryKey: ["admin-events-credential-counts", eventIds.join(",")],
    enabled: eventIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const entries = await Promise.all(
        eventIds.map(async (id) => {
          try {
            const { data, error } = await supabase.functions.invoke("sheet-tab", {
              body: { action: "load", tab: "media", event_id: id },
            });
            if (error || data?.error || data?.denied) return [id, 0] as const;
            const rows = (data?.rows ?? []) as { status?: string }[];
            return [id, rows.filter((r) => r?.status !== "denied").length] as const;
          } catch {
            return [id, 0] as const;
          }
        })
      );
      return Object.fromEntries(entries) as Record<string, number>;
    },
  });

  /**
   * Player totals come from RecruitLook (rl-event-rosters), the same source as
   * the Live Sheet's Rosters tab. This runs as its own query so the cards paint
   * immediately and the number lands when RecruitLook answers. Sum the teams RL
   * gave a number for; if RL doesn't answer at all the total stays null and the
   * card shows "—" rather than asserting a zero.
   */
  const { data: rosterCounts } = useQuery({
    queryKey: ["admin-events-roster-counts", eventIds.join(",")],
    enabled: eventIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const SUPABASE_URL = "https://kvttsygfutwcywroraji.supabase.co";
      const SUPABASE_ANON_KEY =
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt2dHRzeWdmdXR3Y3l3cm9yYWppIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4MTU1OTUsImV4cCI6MjA4NjM5MTU5NX0.wUaZcZxKQpyk2DTzTF2CjYHvWRHa897xW0rleO6mJos";

      const entries = await Promise.all(
        eventIds.map(async (id) => {
          try {
            const res = await fetch(`${SUPABASE_URL}/functions/v1/rl-event-rosters`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                apikey: SUPABASE_ANON_KEY,
                Authorization: `Bearer ${session?.access_token ?? SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({ action: "list", event_id: id }),
            });
            const body = await res.json().catch(() => null);
            if (!res.ok || !body?.ok) return [id, null] as const;
            const counts = Object.values((body.rosters ?? {}) as Record<string, any>)
              .map((r) => r?.player_count)
              .filter((n) => typeof n === "number") as number[];
            const total = counts.length ? counts.reduce((a, b) => a + b, 0) : null;
            return [id, total] as const;
          } catch {
            return [id, null] as const;
          }
        })
      );
      return Object.fromEntries(entries) as Record<string, number | null>;
    },
  });

  /**
   * One batched call for every visible card — the scheduling landing page's own
   * summary action, so the card and the page agree on the state.
   */
  const { data: schedSummary } = useQuery({
    queryKey: ["admin-events-scheduling-summary", eventIds.join(",")],
    enabled: eventIds.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await invokeEdgeFunction("rle-operator-scheduling-readiness", {
        body: { action: "summary", event_ids: eventIds },
      });
      const byId = (data?.ok ? data.data?.events : null) ?? {};
      const map: Record<string, string> = {};
      Object.entries(byId as Record<string, any>).forEach(([id, v]) => {
        if (v?.line) map[id] = v.line;
      });
      return map;

    },
  });



  const opsBadge = (n?: number) =>
    n && n > 0 ? (
      <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none">
        {n}
      </span>
    ) : null;

  const archiveMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabase
        .from("events")
        .update({ is_active: false, is_archived: true, status: "archived" })
        .eq("id", eventId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-events-list"] });
      toast.success("Event archived");
    },
    onError: () => toast.error("Failed to archive event"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabase
        .from("events")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", eventId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-events-list"] });
      toast.success("Event deleted");
    },
    onError: () => toast.error("Failed to delete event"),
  });

  const publishMutation = useMutation({
    mutationFn: async ({ eventId, newStatus }: { eventId: string; newStatus: "draft" | "published" }) => {
      const { error } = await supabase
        .from("events")
        .update({ status: newStatus })
        .eq("id", eventId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin-events-list"] });
      toast.success(vars.newStatus === "published" ? "Event published" : "Event set to draft");
    },
    onError: () => toast.error("Failed to update status"),
  });

  const filteredEvents = (events || []).filter((t) => {
    const matchesSearch =
      !searchQuery ||
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.venue_city || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(t.status);
    return matchesSearch && matchesStatus;
  });

  const toggleStatusFilter = (status: EventStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page header */}
      <div className="border-b border-border bg-background px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h1 className="text-3xl font-bold text-foreground">Events</h1>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => { setCloneSourceId(undefined); setCloneSourceName(undefined); setCloneOpen(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-foreground hover:bg-secondary transition-colors font-medium text-sm"
              >
                <Copy className="w-4 h-4" />
                Clone from previous
              </button>
              <Link
                to="/admin-dashboard/events/new"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                Create Event
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-border bg-background px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search events by name or venue..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-border bg-card pl-10 pr-4 py-2 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
            />
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedStatuses([])}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedStatuses.length === 0 ? "bg-primary text-white" : "bg-secondary text-secondary-foreground hover:bg-muted"
              }`}
            >
              All
            </button>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleStatusFilter(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedStatuses.includes(option.value) ? "bg-primary text-white" : "bg-secondary text-secondary-foreground hover:bg-muted"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border bg-background px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex gap-6">
          {["active", "archived"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-4 px-1 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="space-y-3">
              {filteredEvents.map((ev) => {
                const venue = [ev.venue_city, ev.venue_state].filter(Boolean).join(", ");
                return (
                  <div
                    key={ev.id}
                    className="bg-card rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all border border-border"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
                      <div className="min-w-0 space-y-2">
                        <Link
                          to={`/admin-dashboard/events/${ev.id}`}
                          className="block font-semibold text-lg text-foreground hover:text-primary transition-colors truncate"
                        >
                          {ev.name}
                        </Link>
                        <StatusBadge status={ev.status} />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() =>
                            window.open(
                              `/events/${ev.slug ?? ev.id}`,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          title="Open public event page"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                        >
                          <Eye className="w-4 h-4" /> View
                        </button>
                        <button
                          onClick={() => navigate(`/admin-dashboard/events/${ev.id}/edit`)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
                        >
                          <Edit className="w-4 h-4" /> Edit
                        </button>
                        <button
                          onClick={() => {
                            setCloneSourceId(ev.id);
                            setCloneSourceName(ev.name);
                            setCloneOpen(true);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                        >
                          <Copy className="w-4 h-4" /> Clone
                        </button>
                        <button
                          onClick={() => navigate(`/admin-dashboard/events/${ev.id}/messages`)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                        >
                          <Send className="w-4 h-4" /> Messages
                        </button>
                        {(ev.status === "draft" || ev.status === "published") && (
                          <button
                            onClick={() =>
                              publishMutation.mutate({
                                eventId: ev.id,
                                newStatus: ev.status === "published" ? "draft" : "published",
                              })
                            }
                            disabled={publishMutation.isPending}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-60"
                          >
                            {ev.status === "published" ? <Edit className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            {ev.status === "published" ? "Unpublish" : "Publish"}
                          </button>
                        )}
                        <button
                          onClick={() => archiveMutation.mutate(ev.id)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                        >
                          <Archive className="w-4 h-4" /> Archive
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("Delete this event? This is a soft delete and can be undone.")) {
                              deleteMutation.mutate(ev.id);
                            }
                          }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>

                      </div>
                    </div>

                    <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-[minmax(180px,auto)_minmax(160px,auto)_1fr] sm:items-center">
                      <div className="flex items-center gap-2 min-w-0">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDateRange(ev.start_date, ev.end_date)}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        {venue && (
                          <>
                          <MapPin className="w-4 h-4" />
                            <span className="truncate">{venue}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-foreground sm:text-right">
                        {ev.total_registrations} teams •{" "}
                        {rosterCounts && ev.id in rosterCounts && rosterCounts[ev.id] !== null
                          ? `${rosterCounts[ev.id]} players`
                          : "— players"}
                      </p>
                    </div>

                    <div className="mt-4 border-t border-border pt-4">
                      <button
                        onClick={() => navigate(`/admin-dashboard/events/${ev.id}/teams?view=spreadsheet`)}
                        className="group inline-flex w-full items-center justify-between gap-3 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors sm:w-auto"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Table2 className="w-4 h-4" />
                          Live Sheet
                        </span>
                        <ArrowRight className="w-4 h-4 opacity-80 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => navigate(`/admin-dashboard/events/${ev.id}/scheduling`)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                      >
                        <CalendarClock className="w-4 h-4" />
                        {schedSummary?.[ev.id] ? `Scheduling · ${schedSummary[ev.id]}` : "Scheduling"}
                      </button>
                      <button
                        onClick={() => setOpsTarget({ id: ev.id, panel: "waitlist" })}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                      >
                        <ListOrdered className="w-4 h-4" /> Waitlist
                        {opsBadge(opsCounts?.[ev.id]?.waitlist)}
                      </button>
                      <button
                        onClick={() => setOpsTarget({ id: ev.id, panel: "tracking" })}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                      >
                        <ClipboardCheck className="w-4 h-4" /> Team Tracking
                        {opsBadge(opsCounts?.[ev.id]?.tracking)}
                      </button>
                      <button
                        onClick={() => setOpsTarget({ id: ev.id, panel: "pay_later" })}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                      >
                        <CircleDollarSign className="w-4 h-4" /> Pay Later
                        {opsBadge(opsCounts?.[ev.id]?.payLater)}
                      </button>
                      <button
                        onClick={() => navigate(`/admin-dashboard/events/${ev.id}/registrations`)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                      >
                        <Users className="w-4 h-4" /> Registered Teams
                      </button>
                      <button
                        onClick={() => navigate(`/admin-dashboard/events/${ev.id}/credentials-coaches`)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                      >
                        <IdCard className="w-4 h-4" /> Credentials & Coaches
                        {opsBadge(credentialCounts?.[ev.id] ?? opsCounts?.[ev.id]?.credentials)}
                      </button>
                      <button
                        onClick={() => navigate(`/admin-dashboard/events/${ev.id}/scheduling-requests`)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors"
                      >
                        <CalendarClock className="w-4 h-4" /> Scheduling Requests
                        {opsBadge(opsCounts?.[ev.id]?.scheduling)}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card p-12 text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-semibold mb-1">No events found</p>
              <p className="text-muted-foreground text-sm mb-4">
                Try adjusting your filters or create a new event.
              </p>
              <Link
                to="/admin-dashboard/events/new"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                Create Event
              </Link>
            </div>
          )}
        </div>
      </div>

      <EventOpsDialog
        panel={opsTarget?.panel ?? null}
        eventId={opsTarget?.id}
        onClose={() => setOpsTarget(null)}
      />

      <CloneEventDialog
        open={cloneOpen}
        onOpenChange={setCloneOpen}
        sourceEventId={cloneSourceId}
        sourceEventName={cloneSourceName}
      />
    </div>
  );
}
